import type { CSSInterpolation } from '@emotion/serialize';
import facepaint from 'facepaint';
import { useCallback, useSyncExternalStore } from 'react';

import * as styles from './media-queries.module.scss';

type Facepaint = (...styles: any[]) => CSSInterpolation[];

type CachedMediaQueryList = {
  matchMedia: typeof window.matchMedia;
  mediaQueryList: MediaQueryList;
};

const memoiseMatchMedia = (query: string): ((getWindow?: () => Window) => MediaQueryList) => {
  const cacheByWindow = new WeakMap<Window, CachedMediaQueryList>();
  return (getWindow = () => window) => {
    const ownerWindow = getWindow();
    // eslint-disable-next-line @typescript-eslint/unbound-method -- stored for identity comparison only, never invoked
    const matchMedia = ownerWindow.matchMedia;
    const cached = cacheByWindow.get(ownerWindow);
    if (cached?.matchMedia === matchMedia) {
      return cached.mediaQueryList;
    }
    const mediaQueryList = ownerWindow.matchMedia(query);
    cacheByWindow.set(ownerWindow, { matchMedia, mediaQueryList });
    return mediaQueryList;
  };
};

export const matchSmallTablet = memoiseMatchMedia(styles.smallTabletQuery);
export const matchLargeTablet = memoiseMatchMedia(styles.largeTabletQuery);
export const matchDesktop = memoiseMatchMedia(styles.desktopQuery);
export const matchHeaderDesktop = memoiseMatchMedia(styles.headerDesktopQuery);
export const matchLargeDesktop = memoiseMatchMedia(styles.largeDesktopQuery);
export const matchReducedMotion = memoiseMatchMedia(styles.reducedMotionQuery);
export const matchLandscape = memoiseMatchMedia(styles.landscapeQuery);
export const matchPortrait = memoiseMatchMedia(styles.portraitQuery);
export const matchHover = memoiseMatchMedia(styles.hoverQuery);

const subscribeToMediaQueryList = (
  getMediaQueryList: () => MediaQueryList,
  onChange: () => void
): (() => void) => {
  const mediaQueryList = getMediaQueryList();
  if (!mediaQueryList.addEventListener) {
    // Safari <14 / older WebKit only expose the deprecated `addListener` API.
    mediaQueryList.addListener(onChange);
    return () => mediaQueryList.removeListener(onChange);
  }
  mediaQueryList.addEventListener('change', onChange);
  return () => mediaQueryList.removeEventListener('change', onChange);
};

export const useMatchMedia = (
  getMediaQueryList: (getWindow?: () => Window) => MediaQueryList,
  serverSnapshot: boolean,
  getOwnerWindow?: () => Window
): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) =>
      subscribeToMediaQueryList(() => getMediaQueryList(getOwnerWindow), onChange),
    [getMediaQueryList, getOwnerWindow]
  );

  const getSnapshot = useCallback(
    () => getMediaQueryList(getOwnerWindow).matches,
    [getMediaQueryList, getOwnerWindow]
  );
  const getServerSnapshot = useCallback(() => serverSnapshot, [serverSnapshot]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

const atMedia = (query: string) => `@media ${query}`;

const mqDesktop: Facepaint = facepaint([atMedia(styles.desktopQuery)]);
const mqSmallTabletDesktop: Facepaint = facepaint([
  atMedia(styles.smallTabletQuery),
  atMedia(styles.desktopQuery),
]);
const mqLargeTabletDesktop: Facepaint = facepaint([
  atMedia(styles.largeTabletQuery),
  atMedia(styles.desktopQuery),
]);
const mqLargeDesktop: Facepaint = facepaint([
  atMedia(styles.largeDesktopQuery),
  atMedia(styles.extraLargeDesktopQuery),
]);

export const mq = Object.assign(mqDesktop, {
  withSmallTablet: mqSmallTabletDesktop,
  withLargeTablet: mqLargeTabletDesktop,
  withLargeDesktop: mqLargeDesktop,
});

export const mqReducedMotion: Facepaint = facepaint([atMedia(styles.reducedMotionQuery)]);

export default mq;
